import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  readConsent,
  writeConsent,
  defaultConsent,
  CONSENT_VERSION,
  hasMadeChoice,
} from "../utils/consentStorage";

import { applyConsent } from "../utils/consentGating";
import { isPrerendering } from "../utils/isPrerendering";

const CookieConsentContext = createContext(null);

function getInitialConsent() {
  if (isPrerendering()) {
    return {
      ...defaultConsent,
    };
  }

  try {
    return readConsent();
  } catch (error) {
    console.warn(
      "Unable to read initial cookie consent:",
      error
    );

    return {
      ...defaultConsent,
    };
  }
}

export function CookieConsentProvider({
  children,
}) {
  const initialConsent = useMemo(
    () => getInitialConsent(),
    []
  );

  const [consent, setConsent] =
    useState(initialConsent);

  const [loaded, setLoaded] =
    useState(false);

  const [hasChoice, setHasChoice] =
    useState(() => {
      if (isPrerendering()) {
        return false;
      }

      try {
        return hasMadeChoice(initialConsent);
      } catch {
        return false;
      }
    });

  const [isPrefsOpen, setIsPrefsOpen] =
    useState(false);

  useEffect(() => {
    /*
      Cookie consent is not required while react-snap
      generates static HTML.

      Do not load analytics, marketing scripts or
      browser storage during prerendering.
    */
    if (isPrerendering()) {
      setConsent({
        ...defaultConsent,
      });

      setHasChoice(false);
      setLoaded(true);

      return;
    }

    try {
      const savedConsent = readConsent();

      setConsent(savedConsent);
      setHasChoice(
        hasMadeChoice(savedConsent)
      );

      applyConsent(savedConsent);
    } catch (error) {
      console.error(
        "Cookie consent initialization failed:",
        error
      );

      setConsent({
        ...defaultConsent,
      });

      setHasChoice(false);
    } finally {
      setLoaded(true);
    }
  }, []);

  const updateConsent = (patch) => {
    const next = {
      ...defaultConsent,
      ...consent,
      ...patch,

      necessary: true,
      version: CONSENT_VERSION,
      updatedAt: new Date().toISOString(),
    };

    setConsent(next);
    setHasChoice(true);

    if (!isPrerendering()) {
      try {
        writeConsent(next);
      } catch (error) {
        console.error(
          "Unable to save cookie consent:",
          error
        );
      }

      try {
        applyConsent(next);
      } catch (error) {
        console.error(
          "Unable to apply cookie consent:",
          error
        );
      }
    }

    return next;
  };

  const acceptAll = () =>
    updateConsent({
      preferences: true,
      analytics: true,
      marketing: true,
    });

  const rejectAll = () =>
    updateConsent({
      preferences: false,
      analytics: false,
      marketing: false,
    });

  const openPreferences = () => {
    setIsPrefsOpen(true);
  };

  const closePreferences = () => {
    setIsPrefsOpen(false);
  };

  const value = useMemo(
    () => ({
      consent,
      loaded,
      hasChoice,
      isPrefsOpen,

      setIsPrefsOpen,
      openPreferences,
      closePreferences,

      updateConsent,
      acceptAll,
      rejectAll,
    }),
    [
      consent,
      loaded,
      hasChoice,
      isPrefsOpen,
    ]
  );

  return (
    <CookieConsentContext.Provider
      value={value}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(
    CookieConsentContext
  );

  if (!context) {
    throw new Error(
      "useCookieConsent must be used inside CookieConsentProvider"
    );
  }

  return context;
}